import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { fetchCorrections, fetchMembers, fetchTasks } from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { dapatMemutuskanKoreksi, dapatMengelolaTugas } from '../lib/bel-pemberitahuan';
import { formatDateInZone, formatTimeInZone } from '../lib/format';
import {
  ambangAwal,
  kalimatBagianGagal,
  ringkasanPemberitahuan,
  tugasSelesaiSejak,
} from '../lib/pemberitahuan';
import { namaAnggota, petaNamaAnggota, pengajuKoreksi } from '../lib/pengaju-koreksi';
import { bacaTerakhirDilihat, tulisTerakhirDilihat } from '../lib/terakhir-dilihat';
import { EmptyState, ErrorCard, LoadingState } from '../states';

/**
 * Daftar pemberitahuan — tujuan lonceng di bilah atas.
 *
 * Menyatukan dua hal yang sifatnya berbeda dan karena itu dihitung berbeda (lihat
 * `pemberitahuan.ts`): pengajuan koreksi yang MENUNGGU keputusan, dan tugas yang BARU
 * diselesaikan kurir.
 *
 * Halaman ini tidak memutuskan apa pun. Ia menunjuk: koreksi ke antreannya, tugas ke detailnya.
 * Menggandakan tombol Setujui/Tolak di sini berarti dua tempat yang harus sepakat tentang siapa
 * boleh menekan apa — dan dua tempat seperti itu akan berselisih.
 */
export function PemberitahuanPage(): ReactNode {
  const session = useSession();
  const timezone = session.policy?.timezone ?? 'Asia/Jakarta';
  const bolehKoreksi = dapatMemutuskanKoreksi(session.me.permissions);
  const bolehTugas = dapatMengelolaTugas(session.me.permissions);

  /*
   * Ambang dibaca SEKALI saat halaman dibuka, lalu dibekukan.
   *
   * Kalau ia dibaca ulang tiap render, penandaan "sudah dilihat" di bawah akan langsung
   * mengosongkan daftar yang sedang dibaca orangnya — layar berkedip lalu kosong, dan yang baru
   * saja muncul hilang sebelum sempat dibaca.
   */
  const ambang = useRef<string>(bacaTerakhirDilihat() ?? ambangAwal(new Date()));

  const koreksi = useAsync(
    () => (bolehKoreksi ? fetchCorrections(session.auth, 'PENDING') : Promise.resolve([])),
    [session.auth, bolehKoreksi],
  );
  const tugas = useAsync(
    () => (bolehTugas ? fetchTasks(session.auth, 'completed') : Promise.resolve([])),
    [session.auth, bolehTugas],
  );
  const anggota = useAsync(() => fetchMembers(session.auth), [session.auth]);

  const petaNama = useMemo(
    () => petaNamaAnggota(anggota.state.status === 'ready' ? anggota.state.data : []),
    [anggota.state],
  );

  const baruSelesai = useMemo(
    () =>
      tugas.state.status === 'ready' ? tugasSelesaiSejak(tugas.state.data, ambang.current) : [],
    [tugas.state],
  );

  /*
   * Penandaan terjadi SETELAH daftarnya terbentuk, dan hanya ketika datanya benar-benar sampai.
   * Menandai saat masih memuat akan menghapus pemberitahuan yang belum pernah terlihat siapa pun
   * — kegagalan jaringan berubah menjadi kabar yang hilang diam-diam.
   */
  useEffect(() => {
    if (tugas.state.status === 'ready') {
      tulisTerakhirDilihat(new Date().toISOString());
    }
  }, [tugas.state.status]);

  const jumlahKoreksi = koreksi.state.status === 'ready' ? koreksi.state.data.length : 0;
  const memuat = koreksi.state.status === 'loading' || tugas.state.status === 'loading';

  /*
   * KOSONG YANG JUJUR menuntut dua hal: tidak ada isi, DAN tidak ada bagian yang gagal dimuat.
   *
   * Versi pertama halaman ini hanya menganggapnya gagal bila KEDUA permintaan gagal — sehingga
   * daftar pengajuan yang gagal diambil terbaca sebagai "tidak ada pengajuan". Manajer yang
   * membacanya menyimpulkan tidak ada yang menunggu keputusannya, dan izin sakit yang sesungguhnya
   * menunggu tidak pernah terlihat. Perhitungannya sekarang satu tempat, dipakai bersama panel
   * lonceng.
   */
  const ringkasan = ringkasanPemberitahuan({
    koreksiGagal: koreksi.state.status === 'error',
    tugasGagal: tugas.state.status === 'error',
    jumlahKoreksi,
    jumlahTugas: baruSelesai.length,
  });
  const peringatan = kalimatBagianGagal(ringkasan.bagianGagal);
  const gagalSemua = koreksi.state.status === 'error' && tugas.state.status === 'error';
  const kosong = !memuat && ringkasan.kosongSungguhan;

  return (
    <>
      <section className="card">
        <h2>Pemberitahuan</h2>
        <p className="muted small">
          Pengajuan yang menunggu keputusan Anda, dan tugas yang baru diselesaikan kurir sejak
          terakhir halaman ini dibuka.
        </p>
      </section>

      {memuat ? <LoadingState label="Memuat pemberitahuan…" /> : null}

      {peringatan && !gagalSemua ? (
        <section className="card">
          <p className="warn-text">{peringatan}</p>
        </section>
      ) : null}

      {gagalSemua && koreksi.state.status === 'error' ? (
        <ErrorCard
          error={koreksi.state.error}
          onRetry={() => {
            koreksi.reload();
            tugas.reload();
          }}
        />
      ) : null}

      {kosong ? (
        <EmptyState
          title="Tidak ada pemberitahuan baru"
          hint="Pengajuan koreksi dan penyelesaian tugas akan muncul di sini."
        />
      ) : null}

      {koreksi.state.status === 'ready' && koreksi.state.data.length > 0 ? (
        <section className="card">
          <div className="row space-between">
            <h3>Menunggu keputusan</h3>
            <Link to="/attendance/corrections">Buka antrean</Link>
          </div>
          <ul className="list">
            {koreksi.state.data.map((k) => {
              const siapa = pengajuKoreksi(petaNama, k);
              return (
                <li key={k.id} className="list-item">
                  <strong>{siapa.subjek}</strong>
                  <p className="muted small">
                    Hari kerja {formatDateInZone(`${k.workDate}T00:00:00.000Z`, 'UTC')} · diajukan{' '}
                    {formatTimeInZone(k.createdAt, timezone)}
                    {siapa.diajukanOleh ? ` · oleh ${siapa.diajukanOleh}` : ''}
                  </p>
                  <p>{k.reason}</p>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {baruSelesai.length > 0 ? (
        <section className="card">
          <h3>Baru diselesaikan kurir</h3>
          <ul className="list">
            {baruSelesai.map((t) => (
              <li key={t.id} className="list-item">
                <Link to={`/tasks/${t.id}`}>
                  <strong>{t.title}</strong>
                </Link>
                <p className="muted small">
                  {t.taskNumber}
                  {t.assigneeUserId ? ` · ${namaAnggota(petaNama, t.assigneeUserId)}` : ''}
                  {t.completedAt ? ` · selesai ${formatTimeInZone(t.completedAt, timezone)}` : ''}
                </p>
                {t.destinationName ? <p className="muted small">{t.destinationName}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}
